<template>
  <el-dialog
    v-model="visible"
    title="选择图片"
    width="min(1120px, 96vw)"
    top="4vh"
    class="material-picker-dialog"
    destroy-on-close
    append-to-body
    @closed="onClosed"
  >
    <div class="picker-layout">
      <aside class="picker-sidebar">
        <div class="picker-sidebar__head">
          <span>文件夹</span>
          <el-button link type="primary" size="small" @click="refreshSidebar">刷新</el-button>
        </div>

        <template v-if="isSuperAdmin && !superAdminInDesignerView">
          <div class="picker-designer-search">
            <el-input
              v-model="designerKeyword"
              placeholder="搜索设计师"
              clearable
              size="small"
              @keyup.enter="searchDesigners"
              @clear="searchDesigners"
            />
          </div>
          <div v-loading="designerLoading" class="picker-designer-list">
            <div
              v-for="item in designerList"
              :key="item.id"
              class="picker-designer-item"
              @click="selectDesigner(item)"
            >
              {{ designerDisplayName(item) }}
            </div>
            <el-empty v-if="!designerLoading && !designerList.length" description="暂无设计师" :image-size="48" />
          </div>
          <div v-if="designerPage.total > designerPage.size" class="picker-designer-pagination">
            <el-pagination
              small
              layout="prev, pager, next"
              :current-page="designerPage.current"
              :page-size="designerPage.size"
              :total="designerPage.total"
              @current-change="changeDesignerPage"
            />
          </div>
        </template>

        <template v-else>
          <div v-if="isSuperAdmin && superAdminInDesignerView" class="picker-designer-back" @click="backToDesignerList">
            ← {{ activeDesignerName }}
          </div>
          <div v-loading="folderLoading" class="picker-folder-tree">
            <el-tree
              v-if="folderTree.length"
              ref="folderTreeRef"
              :key="folderTreeRenderKey"
              class="picker-folder-tree__inner"
              :data="folderTree"
              node-key="id"
              :props="{ label: 'label', children: 'children' }"
              highlight-current
              default-expand-all
              @node-click="onFolderClick"
            />
            <el-empty v-else description="暂无文件夹" :image-size="48" />
          </div>
        </template>
      </aside>

      <section class="picker-main">
        <div class="picker-main__head">
          <span class="picker-main__title">{{ selectedFolderLabel || '全部素材' }}</span>
          <span v-if="selectedNodeType === 'folder'" class="picker-main__hint">当前目录下新建/上传</span>
        </div>
        <div class="picker-toolbar">
          <el-upload
            :action="uploadAction"
            name="file"
            :show-file-list="false"
            :data="uploadExtraData"
            :headers="uploadHeaders"
            :accept="uploadAccept"
            :before-upload="beforeUpload"
            :on-success="onUploadSuccess"
            :on-error="onUploadError"
            :disabled="!canWrite"
            multiple
          >
            <el-button size="small" type="primary" :disabled="!canWrite">上传本地图片</el-button>
          </el-upload>
          <el-button size="small" :disabled="!canWrite" @click="openCreateFolder">新建文件夹</el-button>
          <el-dropdown trigger="click" @command="handleMoreCommand">
            <el-button size="small">
              <el-icon class="toolbar-more-icon"><MoreFilled /></el-icon>
              更多操作
            </el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="move" :disabled="!canMoveImages">移动到</el-dropdown-item>
                <el-dropdown-item command="download" :disabled="!pickedItems.length">下载</el-dropdown-item>
                <el-dropdown-item command="delete" :disabled="!pickedItems.length || !canWrite">删除</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <el-input
            v-model="keyword"
            placeholder="搜索文件名/标签/路径"
            clearable
            size="small"
            class="picker-search"
            @keyup.enter="applySearch"
            @clear="applySearch"
          />
          <el-button size="small" @click="applySearch">搜索</el-button>
          <el-radio-group v-model="viewMode" size="small" class="picker-view-toggle">
            <el-radio-button label="table">列表</el-radio-button>
            <el-radio-button label="grid">网格</el-radio-button>
          </el-radio-group>
        </div>

        <div class="picker-selected-tip">
          已勾选 {{ pickedItems.length }} 项，当前字段确认时最多选择 {{ limit }} 项
        </div>

        <div class="picker-body" :class="{ 'picker-body--grid': viewMode === 'grid' }">
        <div v-if="viewMode === 'grid'" class="picker-grid" v-loading="loading">
          <div
            v-for="folder in currentSubFolders"
            :key="folder.id"
            class="picker-card picker-card--folder"
            @click="enterFolderCard(folder)"
          >
            <div class="picker-card__media picker-card__media--folder">
              <el-icon class="picker-folder-icon"><Folder /></el-icon>
            </div>
            <div class="picker-card__footer" @click.stop>
              <MaterialInlineNameCell
                :row="{ rowType: 'folder', folder, key: `folder-${folder.id}` }"
                :editable="canWrite"
                :design-id="activeDesignId"
                @success="onInlineRenameSuccess"
              />
              <div class="picker-card__sub">-</div>
            </div>
          </div>
          <div
            v-for="item in imageList"
            :key="item.id"
            class="picker-card"
            :class="{ selected: isSelected(item) }"
            @click="toggleItem(item)"
          >
            <div class="picker-card__media">
              <img :src="displayThumb(item)" alt="" loading="lazy" decoding="async" />
              <el-checkbox
                class="picker-card__check"
                :model-value="isSelected(item)"
                @click.stop
                @change="(val) => setSelected(item, val)"
              />
            </div>
            <div class="picker-card__footer" @click.stop>
              <MaterialInlineNameCell
                :row="{ rowType: 'file', item, key: `file-${item.id}` }"
                :editable="canWrite"
                :design-id="activeDesignId"
                @success="onInlineRenameSuccess"
              />
              <div class="picker-card__sub">{{ formatSize(item.fileSize) || '-' }}</div>
            </div>
          </div>
          <el-empty v-if="!loading && !currentSubFolders.length && !imageList.length" description="暂无图片" />
        </div>

        <div v-else class="picker-table-wrap" v-loading="loading">
          <el-table
            :data="tableRows"
            size="small"
            height="100%"
            row-key="key"
            highlight-current-row
            class="picker-table picker-table--xhs"
            :row-class-name="tableRowClassName"
            @row-click="onTableRowClick"
          >
            <el-table-column width="48" align="center">
              <template #default="{ row }">
                <el-checkbox
                  v-if="row.rowType === 'image'"
                  :model-value="isSelected(row.item)"
                  @click.stop
                  @change="(val) => setSelected(row.item, val)"
                />
              </template>
            </el-table-column>
            <el-table-column label="图片" width="88" align="center">
              <template #default="{ row }">
                <div v-if="row.rowType === 'folder'" class="picker-table-folder">
                  <el-icon class="picker-table-folder__icon"><Folder /></el-icon>
                </div>
                <img v-else :src="displayThumb(row.item)" class="picker-table-thumb" alt="" loading="lazy" decoding="async" />
              </template>
            </el-table-column>
            <el-table-column label="名称" min-width="200">
              <template #default="{ row }">
                <MaterialInlineNameCell
                  :row="row"
                  :editable="canWrite"
                  :design-id="activeDesignId"
                  @success="onInlineRenameSuccess"
                />
              </template>
            </el-table-column>
            <el-table-column label="大小" width="96" align="center">
              <template #default="{ row }">
                {{ row.rowType === 'folder' ? '-' : formatSize(row.item.fileSize) }}
              </template>
            </el-table-column>
            <el-table-column label="尺寸" width="110" align="center">
              <template #default="{ row }">
                {{ row.rowType === 'folder' ? '-' : (row.item.dimensionLabel || formatDimension(row.item)) }}
              </template>
            </el-table-column>
            <el-table-column label="上传时间" width="150" align="center">
              <template #default="{ row }">
                {{ row.rowType === 'folder' ? '-' : formatCreateTime(row.item) }}
              </template>
            </el-table-column>
            <el-table-column label="是否引用" width="96" align="center">
              <template #default="{ row }">
                <template v-if="row.rowType === 'folder'">-</template>
                <el-tag v-else size="small" :type="isItemReferenced(row.item) ? 'primary' : 'info'" effect="light">
                  {{ isItemReferenced(row.item) ? '已引用' : '未引用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="148" fixed="right" class-name="op-col">
              <template #default="{ row }">
                <div class="op-cell">
                  <template v-if="row.rowType === 'folder'">
                    <el-button link type="primary" size="small" @click.stop="openFolderMoveFromRow(row.folder)">移动到</el-button>
                    <el-button link type="danger" size="small" @click.stop="removeFolderFromRow(row.folder)">删除</el-button>
                  </template>
                  <template v-else>
                    <el-button link type="primary" size="small" @click.stop="openMoveItemFromRow(row.item)">移动到</el-button>
                    <el-button link type="primary" size="small" @click.stop="downloadSingleItem(row.item)">下载</el-button>
                    <el-button link type="danger" size="small" @click.stop="removeSingleItem(row.item)">删除</el-button>
                  </template>
                </div>
              </template>
            </el-table-column>
          </el-table>
          <el-empty v-if="!loading && !tableRows.length" description="暂无图片" />
        </div>
        </div>

        <div class="picker-footer">
          <el-pagination
            small
            background
            layout="total, prev, pager, next, sizes"
            :current-page="pageState.current"
            :page-size="pageState.size"
            :page-sizes="[24, 48, 96]"
            :total="pageState.total"
            @current-change="changePage"
            @size-change="changeSize"
          />
          <div class="picker-footer__actions">
            <el-button @click="visible = false">取消</el-button>
            <el-button type="primary" :disabled="!pickedItems.length" @click="handleConfirm">确认</el-button>
          </div>
        </div>
      </section>
    </div>

    <el-dialog v-model="createFolderVisible" title="新建文件夹" width="420px" append-to-body destroy-on-close @closed="newFolderName = ''">
      <p class="picker-dialog-tip">将创建在「{{ selectedFolderLabel || '全部素材' }}」下</p>
      <el-input v-model="newFolderName" placeholder="请输入文件夹名称" maxlength="64" show-word-limit />
      <template #footer>
        <el-button @click="createFolderVisible = false">取消</el-button>
        <el-button type="primary" :loading="folderSubmitLoading" @click="submitCreateFolder">确定</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="moveVisible" :title="moveMode === 'folder' ? '移动文件夹到' : '移动到'" width="480px" append-to-body destroy-on-close @opened="resetMoveTarget">
      <p class="picker-dialog-tip">{{ moveMode === 'folder' ? '选择目标位置，文件夹及其子文件夹将一起移动' : `将 ${pickedItems.length} 张图片移动到目标文件夹` }}</p>
      <el-tree
        v-if="moveVisible"
        ref="moveTreeRef"
        :key="'move-' + folderTreeRenderKey"
        class="picker-move-tree"
        :data="moveFolderTree"
        node-key="id"
        :props="{ label: 'label', children: 'children' }"
        highlight-current
        default-expand-all
        @node-click="onMoveTargetClick"
      />
      <template #footer>
        <el-button @click="moveVisible = false">取消</el-button>
        <el-button type="primary" :loading="moveLoading" @click="submitMove">确定</el-button>
      </template>
    </el-dialog>
  </el-dialog>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Folder, MoreFilled } from '@element-plus/icons-vue'
import { getAuthToken, isSuperAdmin as checkSuperAdmin } from '@/utils/auth'
import { normalizeImgUrl, materialThumbUrl, parseImageLibraryUploadResponse } from '@/utils/mediaUrl'
import { listChildFolderNodes, resolveListFolderId } from '@/utils/imageLibraryFolders'
import MaterialInlineNameCell from '@/components/MaterialInlineNameCell.vue'
import {
  DEFAULT_ALLOWED_EXTENSIONS,
  allowedExtensionsMessage,
  buildUploadAccept,
  getFileExtension,
  isAllowedUploadFile,
  loadAllowedExtensions,
} from '@/utils/uploadExtensions'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  multiple: { type: Boolean, default: true },
  limit: { type: Number, default: 10 },
  selectedCountBase: { type: Number, default: 0 },
})

const emit = defineEmits(['update:modelValue', 'confirm'])

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const uploadAction = '/api/image-libraries/upload'
const loading = ref(false)
const list = ref([])
const folderTree = ref([])
const folderTreeRef = ref(null)
const folderTreeRenderKey = ref(0)
const folderLoading = ref(false)
const selectedFolderId = ref(null)
const selectedDesignId = ref(null)
const selectedFolderKey = ref('all')
const selectedFolderLabel = ref('全部素材')
const selectedNodeType = ref('all')
const libraryContext = ref({ superAdmin: false, designId: null })
const superAdminInDesignerView = ref(false)
const activeDesignerName = ref('')
const designerList = ref([])
const designerKeyword = ref('')
const designerLoading = ref(false)
const designerPage = ref({ current: 1, size: 15, total: 0 })
const pageState = ref({ current: 1, size: 24, total: 0 })
const keyword = ref('')
const viewMode = ref('grid')
const pickedItems = ref([])
const createFolderVisible = ref(false)
const newFolderName = ref('')
const folderSubmitLoading = ref(false)
const moveVisible = ref(false)
const moveLoading = ref(false)
const moveMode = ref('image')
const moveTreeRef = ref(null)
const moveTargetKey = ref('all')
const moveTargetFolderId = ref(null)
const moveTargetDesignId = ref(null)
const allowedExtensions = ref(DEFAULT_ALLOWED_EXTENSIONS)

const PICKER_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg']

const isSuperAdmin = computed(() => checkSuperAdmin())
const activeDesignId = computed(() => {
  if (!isSuperAdmin.value) return libraryContext.value.designId
  return superAdminInDesignerView.value ? selectedDesignId.value : null
})
const canWrite = computed(() => {
  if (!isSuperAdmin.value) return !!libraryContext.value.designId
  return superAdminInDesignerView.value && !!selectedDesignId.value
})
const canMoveImages = computed(() => pickedItems.value.length > 0 && canWrite.value)
const canManageFolder = computed(() => selectedNodeType.value === 'folder' && selectedFolderId.value)
const uploadAccept = computed(() => buildUploadAccept(allowedExtensions.value, PICKER_IMAGE_EXTENSIONS))
const createFolderParentId = computed(() => {
  if (selectedNodeType.value !== 'folder' || !selectedFolderId.value) return 0
  return Number(selectedFolderId.value)
})

const cloneFolderTree = (nodes) => (nodes || []).map((node) => ({
  id: node.id,
  label: node.label,
  folderId: node.folderId,
  designId: node.designId,
  nodeType: node.nodeType,
  pathPrefix: node.pathPrefix,
  children: cloneFolderTree(node.children),
}))

const moveFolderTree = computed(() => cloneFolderTree(folderTree.value))
const imageList = computed(() => (list.value || []).filter((item) => isImage(item)))
const currentSubFolders = computed(() => {
  if (keyword.value) return []
  return listChildFolderNodes(folderTree.value, selectedFolderKey.value, selectedNodeType.value)
})
const tableRows = computed(() => {
  const folders = currentSubFolders.value.map((folder) => ({
    rowType: 'folder',
    folder,
    key: `folder-${folder.id}`,
  }))
  const images = imageList.value.map((item) => ({
    rowType: 'image',
    item,
    key: `image-${item.id}`,
  }))
  return [...folders, ...images]
})
const uploadHeaders = computed(() => {
  const token = getAuthToken()
  return token ? { Authorization: `Bearer ${token}`, Token: token } : {}
})

const uploadExtraData = computed(() => {
  const data = {}
  if (activeDesignId.value) data.designId = activeDesignId.value
  if (selectedFolderId.value) data.folderId = selectedFolderId.value
  return data
})

const displayUrl = (path) => normalizeImgUrl(path)

const displayThumb = (item) => {
  if (!item?.id) return displayUrl(item?.savePath)
  return materialThumbUrl(item.id, 240)
}

const isImage = (row) => {
  const t = String(row?.type || '').toLowerCase()
  if (t.startsWith('image/')) return true
  const p = String(row?.savePath || '').toLowerCase()
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(p)
}

const displayFileName = (item) => {
  const name = String(item?.fileName || '').trim()
  if (name) return name
  const path = String(item?.savePath || '')
  return path.split('/').pop() || '未命名'
}

const formatDimension = (row) => {
  if (row?.width > 0 && row?.height > 0) return `${row.width} x ${row.height}`
  return '-'
}

const formatSize = (size) => {
  const n = Number(size) || 0
  if (!n) return '-'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(2)} MB`
}

const formatCreateTime = (item) => {
  if (!item?.createTime) return '-'
  return String(item.createTime).replace('T', ' ').slice(0, 16)
}

const isItemReferenced = (item) => {
  if (!item) return false
  if (item.referenced === true) return true
  const count = Number(item.referenceCount)
  return Number.isFinite(count) && count > 0
}

const formatReferenced = (item) => (isItemReferenced(item) ? '已引用' : '未引用')

const tableRowClassName = ({ row }) => (row?.rowType === 'folder' ? 'picker-table-row--folder' : '')

const onTableRowClick = (row) => {
  if (!row) return
  if (row.rowType === 'folder') {
    enterFolderCard(row.folder)
    return
  }
  toggleItem(row.item)
}

const designerDisplayName = (item) => item?.artistName || item?.realName || item?.studioName || item?.createUserName || `设计师${item?.id}`

const isSelected = (item) => pickedItems.value.some((x) => String(x.id) === String(item.id))

const setSelected = (item, checked) => {
  const id = String(item.id)
  if (checked) {
    if (!isSelected(item)) pickedItems.value.push(item)
  } else {
    pickedItems.value = pickedItems.value.filter((x) => String(x.id) !== id)
  }
}

const toggleItem = (item) => setSelected(item, !isSelected(item))

const loadContext = async () => {
  try {
    const res = await axios.get('/api/image-libraries/context')
    libraryContext.value = res?.data?.data || { superAdmin: false, designId: null }
    if (!isSuperAdmin.value && libraryContext.value.designId) {
      selectedDesignId.value = libraryContext.value.designId
    }
  } catch {
    libraryContext.value = { superAdmin: false, designId: null }
  }
}

const loadDesigners = async () => {
  if (!isSuperAdmin.value) return
  designerLoading.value = true
  try {
    const res = await axios.get('/api/designers/list', {
      params: {
        current: designerPage.value.current,
        size: designerPage.value.size,
        keyword: designerKeyword.value || undefined,
      },
    })
    const data = res?.data?.data || {}
    designerList.value = data.records || []
    designerPage.value.total = Number(data.total) || 0
  } catch {
    designerList.value = []
  } finally {
    designerLoading.value = false
  }
}

const searchDesigners = () => {
  designerPage.value.current = 1
  loadDesigners()
}

const changeDesignerPage = (page) => {
  designerPage.value.current = page
  loadDesigners()
}

const onInlineRenameSuccess = async () => {
  await loadFolderTree()
  loadData()
}

const selectDesigner = async (designer) => {
  if (!designer?.id) return
  superAdminInDesignerView.value = true
  activeDesignerName.value = designerDisplayName(designer)
  selectedDesignId.value = designer.id
  selectedFolderId.value = null
  selectedFolderKey.value = 'all'
  selectedFolderLabel.value = '全部素材'
  selectedNodeType.value = 'all'
  pageState.value.current = 1
  await loadFolderTree()
  loadData()
}

const backToDesignerList = async () => {
  superAdminInDesignerView.value = false
  activeDesignerName.value = ''
  selectedDesignId.value = null
  selectedFolderId.value = null
  folderTree.value = []
  pageState.value.current = 1
  await loadDesigners()
  loadData()
}

const loadFolderTree = async () => {
  if (isSuperAdmin.value && !superAdminInDesignerView.value) {
    folderTree.value = []
    return
  }
  folderLoading.value = true
  try {
    const params = {}
    if (isSuperAdmin.value && selectedDesignId.value) params.designId = selectedDesignId.value
    const res = await axios.get('/api/image-libraries/folders', { params })
    folderTree.value = res?.data?.data || []
    if (!folderTree.value.length) {
      folderTree.value = [{ id: 'all', label: '全部素材', nodeType: 'all', children: [] }]
    }
  } catch {
    folderTree.value = [{ id: 'all', label: '全部素材', nodeType: 'all', children: [] }]
  } finally {
    folderLoading.value = false
    folderTreeRenderKey.value += 1
    await nextTick()
    folderTreeRef.value?.setCurrentKey?.(selectedFolderKey.value || 'all')
  }
}

const onFolderClick = (node) => {
  if (!node) return
  selectedFolderKey.value = node.id || 'all'
  selectedFolderLabel.value = node.label || '全部素材'
  selectedNodeType.value = node.nodeType || 'all'
  selectedFolderId.value = node.nodeType === 'folder' ? (node.folderId || null) : null
  if (node.designId) selectedDesignId.value = node.designId
  pageState.value.current = 1
  loadData()
  nextTick(() => folderTreeRef.value?.setCurrentKey?.(selectedFolderKey.value || 'all'))
}

const enterFolderCard = (folder) => {
  if (!folder) return
  onFolderClick(folder)
}

const activateFolderRow = (folder) => {
  if (!folder) return
  selectedFolderKey.value = folder.id || 'all'
  selectedNodeType.value = folder.nodeType || 'folder'
  selectedFolderLabel.value = folder.label || '全部素材'
  selectedFolderId.value = folder.folderId || null
  if (folder.designId) selectedDesignId.value = folder.designId
}

const openFolderMoveFromRow = (folder) => {
  activateFolderRow(folder)
  openMoveDialog('folder')
}

const removeFolderFromRow = async (folder) => {
  if (!folder?.folderId) return
  try {
    await ElMessageBox.confirm(`确认将文件夹「${folder.label}」及其子文件夹、素材移入回收站？`, '提示', { type: 'warning' })
    await axios.post('/api/image-libraries/folders/remove', null, {
      params: { id: folder.folderId, designId: activeDesignId.value },
    })
    ElMessage.success('已移入回收站')
    if (String(selectedFolderKey.value) === String(folder.id)) {
      selectedFolderKey.value = 'all'
      selectedNodeType.value = 'all'
      selectedFolderId.value = null
      selectedFolderLabel.value = '全部素材'
    }
    await loadFolderTree()
    loadData()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(e?.response?.data?.msg || '删除失败')
  }
}

const openMoveItemFromRow = (item) => {
  if (!item?.id) return
  pickedItems.value = [item]
  openMoveDialog('image')
}

const removeSingleItem = async (item) => {
  if (!item?.id) return
  try {
    await ElMessageBox.confirm('确认将该素材移入回收站？', '提示', { type: 'warning' })
    await axios.post('/api/image-libraries/remove', null, {
      params: { ids: String(item.id), designId: activeDesignId.value || undefined },
    })
    ElMessage.success('已移入回收站')
    pickedItems.value = pickedItems.value.filter((x) => String(x.id) !== String(item.id))
    loadData()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error('删除失败')
  }
}

const handleMoreCommand = (command) => {
  if (command === 'move') openMoveDialog()
  else if (command === 'download') handleDownload()
  else if (command === 'delete') handleRemove()
}

const handleDownload = async (idsOverride) => {
  const ids = idsOverride || pickedItems.value.map((item) => String(item.id))
  if (!ids.length) {
    ElMessage.warning('请先选择要下载的素材')
    return
  }
  try {
    const res = await axios.get('/api/image-libraries/download-zip', {
      params: { ids: ids.join(','), designId: activeDesignId.value || undefined },
      responseType: 'blob',
    })
    const blob = new Blob([res.data], { type: 'application/zip' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `materials_${Date.now()}.zip`
    link.click()
    window.URL.revokeObjectURL(url)
    ElMessage.success('下载已开始')
  } catch {
    ElMessage.error('下载失败')
  }
}

const downloadSingleItem = (item) => {
  if (!item?.id) return
  handleDownload([String(item.id)])
}

const handleRemove = async () => {
  if (!pickedItems.value.length) return
  try {
    await ElMessageBox.confirm(`确认将选中的 ${pickedItems.value.length} 条素材移入回收站？`, '提示', { type: 'warning' })
    await axios.post('/api/image-libraries/remove', null, {
      params: {
        ids: pickedItems.value.map((item) => item.id).join(','),
        designId: activeDesignId.value || undefined,
      },
    })
    ElMessage.success('已移入回收站')
    pickedItems.value = []
    loadData()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error('删除失败')
  }
}

const openCreateFolder = () => {
  if (!canWrite.value) {
    ElMessage.warning(isSuperAdmin.value ? '请先选择设计师' : '当前账号未关联设计师')
    return
  }
  newFolderName.value = ''
  createFolderVisible.value = true
}

const submitCreateFolder = async () => {
  const name = String(newFolderName.value || '').trim()
  if (!name) {
    ElMessage.warning('请输入文件夹名称')
    return
  }
  folderSubmitLoading.value = true
  try {
    await axios.post('/api/image-libraries/folders/create', {
      parentId: createFolderParentId.value,
      designId: activeDesignId.value,
      name,
    })
    ElMessage.success('文件夹已创建')
    createFolderVisible.value = false
    await loadFolderTree()
  } catch (e) {
    ElMessage.error(e?.response?.data?.msg || '创建失败')
  } finally {
    folderSubmitLoading.value = false
  }
}

const resetMoveTarget = async () => {
  const tree = moveFolderTree.value
  const first = tree[0]
  moveTargetKey.value = first?.id || 'all'
  moveTargetFolderId.value = null
  moveTargetDesignId.value = activeDesignId.value
  await nextTick()
  moveTreeRef.value?.setCurrentKey?.(moveTargetKey.value)
}

const onMoveTargetClick = (node) => {
  if (!node) return
  moveTargetKey.value = node.id || 'all'
  moveTargetFolderId.value = node.nodeType === 'folder' ? (node.folderId || null) : null
  moveTargetDesignId.value = node.designId || activeDesignId.value
}

const openMoveDialog = (mode = 'image') => {
  if (mode === 'folder') {
    if (!canManageFolder.value) return
    moveMode.value = 'folder'
    moveVisible.value = true
    return
  }
  if (!pickedItems.value.length) {
    ElMessage.warning('请先勾选要移动的图片')
    return
  }
  moveMode.value = 'image'
  moveVisible.value = true
}

const submitMove = async () => {
  moveLoading.value = true
  try {
    if (moveMode.value === 'folder') {
      if (!selectedFolderId.value) return
      await axios.post('/api/image-libraries/folders/move', {
        id: selectedFolderId.value,
        parentId: moveTargetFolderId.value || 0,
        designId: activeDesignId.value,
      })
      ElMessage.success('文件夹已移动')
      moveVisible.value = false
      await loadFolderTree()
      loadData()
      return
    }
    if (!pickedItems.value.length) return
    await axios.post('/api/image-libraries/move', {
      ids: pickedItems.value.map((item) => item.id).join(','),
      designId: activeDesignId.value,
      folderId: moveTargetFolderId.value || '',
    })
    ElMessage.success('移动成功')
    moveVisible.value = false
    pickedItems.value = []
    await loadFolderTree()
    loadData()
  } catch (e) {
    ElMessage.error(e?.response?.data?.msg || '移动失败')
  } finally {
    moveLoading.value = false
  }
}

const loadData = async () => {
  loading.value = true
  try {
    const params = {
      current: pageState.value.current,
      size: pageState.value.size,
      keyword: keyword.value || undefined,
      designId: selectedDesignId.value || undefined,
      folderId: resolveListFolderId(
        selectedNodeType.value,
        selectedFolderId.value,
        { globalAllView: isSuperAdmin.value && !superAdminInDesignerView.value },
      ),
    }
    const res = await axios.get('/api/image-libraries/list', { params })
    const data = res?.data?.data || {}
    list.value = data.records || []
    pageState.value.total = Number(data.total) || 0
  } catch {
    ElMessage.error('加载素材失败')
  } finally {
    loading.value = false
  }
}

const refreshSidebar = async () => {
  if (isSuperAdmin.value && !superAdminInDesignerView.value) {
    await loadDesigners()
    return
  }
  await loadFolderTree()
}

const applySearch = () => {
  pageState.value.current = 1
  loadData()
}

const changePage = (p) => {
  pageState.value.current = p
  loadData()
}

const changeSize = (s) => {
  pageState.value.size = s
  pageState.value.current = 1
  loadData()
}

const beforeUpload = (file) => {
  if (!canWrite.value) {
    ElMessage.warning(isSuperAdmin.value ? '请先选择设计师' : '当前账号未关联设计师')
    return false
  }
  if (!isAllowedUploadFile(file, allowedExtensions.value)) {
    ElMessage.warning(allowedExtensionsMessage(allowedExtensions.value))
    return false
  }
  if (!PICKER_IMAGE_EXTENSIONS.includes(getFileExtension(file))) {
    ElMessage.warning('仅支持上传图片')
    return false
  }
  if (file.size > 100 * 1024 * 1024) {
    ElMessage.warning('单文件不能超过 100MB')
    return false
  }
  return true
}

const onUploadSuccess = (resp) => {
  const result = parseImageLibraryUploadResponse(resp)
  if (result.ok) {
    ElMessage.success('上传成功')
    pageState.value.current = 1
    loadData()
    loadFolderTree()
    return
  }
  ElMessage.error(result.msg)
}

const onUploadError = () => {
  ElMessage.error('上传失败')
}

const handleConfirm = async () => {
  const remain = props.limit - props.selectedCountBase
  const confirmLimit = props.multiple ? Math.max(0, remain) : Math.min(1, Math.max(0, remain))
  if (pickedItems.value.length > confirmLimit) {
    ElMessage.warning(`当前字段最多选择 ${props.limit} 张图片，请减少勾选后再确认`)
    return
  }
  const items = pickedItems.value.slice(0, confirmLimit)
  const paths = items.map((item) => item.savePath).filter(Boolean)
  const ids = items.map((item) => item.id).filter((id) => id != null)
  if (ids.length) {
    try {
      await axios.post('/api/image-libraries/reference/increment', { ids })
    } catch {
      // 引用标记失败不阻断选图
    }
  }
  emit('confirm', paths)
  visible.value = false
}

const onClosed = () => {
  pickedItems.value = []
  keyword.value = ''
}

const initDialog = async () => {
  pickedItems.value = []
  keyword.value = ''
  pageState.value.current = 1
  selectedNodeType.value = 'all'
  selectedFolderKey.value = 'all'
  selectedFolderLabel.value = '全部素材'
  selectedFolderId.value = null
  allowedExtensions.value = await loadAllowedExtensions()
  await loadContext()
  if (isSuperAdmin.value) {
    superAdminInDesignerView.value = false
    await loadDesigners()
  } else {
    await loadFolderTree()
  }
  loadData()
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) initDialog()
  }
)
</script>

<style scoped>
.material-picker-dialog :deep(.el-dialog) {
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  margin: 4vh auto;
}
.material-picker-dialog :deep(.el-dialog__body) {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding-top: 8px;
}
.picker-layout {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 12px;
  height: min(72vh, 760px);
  min-height: 480px;
  overflow: hidden;
}
.picker-sidebar {
  width: 220px;
  flex-shrink: 0;
  min-height: 0;
  align-self: stretch;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.picker-sidebar__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  font-weight: 600;
}
.picker-designer-search {
  padding: 8px 10px;
}
.picker-designer-list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 6px;
}
.picker-folder-tree {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 6px;
  background: #fff;
}
.picker-folder-tree__inner :deep(.el-tree-node__content) {
  height: 34px;
  border-radius: 6px;
}
.picker-folder-tree__inner :deep(.el-tree-node.is-current > .el-tree-node__content) {
  background: #eff6ff;
  color: #2563eb;
  font-weight: 600;
}
.picker-main__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.picker-main__title {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.picker-main__hint {
  font-size: 12px;
  color: #2563eb;
}
.picker-dialog-tip {
  margin: 0 0 12px;
  font-size: 13px;
  color: #6b7280;
}
.picker-move-tree {
  max-height: 360px;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px;
}
.picker-move-tree :deep(.el-tree-node.is-current > .el-tree-node__content) {
  background: #eff6ff;
  color: #2563eb;
}
.picker-designer-pagination {
  padding: 4px 6px 8px;
  border-top: 1px solid #f3f4f6;
  flex-shrink: 0;
}
.picker-designer-pagination :deep(.el-pagination) {
  justify-content: center;
}
.picker-designer-item,
.picker-designer-back {
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #374151;
}
.picker-designer-item:hover,
.picker-designer-back:hover {
  background: #f3f4f6;
}
.picker-designer-back {
  margin: 8px;
  background: #eff6ff;
  color: #2563eb;
}
.picker-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #fafafa;
}
.picker-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
}
.picker-body--grid {
  overflow-x: hidden;
  overflow-y: auto;
  display: block;
}
.picker-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.toolbar-more-icon {
  margin-right: 4px;
}
.picker-view-toggle {
  margin-left: auto;
}
.picker-search {
  width: 220px;
}
.picker-selected-tip {
  padding: 8px 12px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 13px;
  flex-shrink: 0;
}
.picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  grid-auto-rows: minmax(188px, auto);
  align-items: start;
  align-content: start;
  justify-content: start;
  gap: 12px;
  padding: 12px;
  box-sizing: border-box;
}
.picker-table-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  padding: 0 12px 12px;
}
.picker-table {
  flex: 1;
  min-height: 0;
}
.picker-table--xhs :deep(.el-table__header th) {
  background: #fafafa;
  color: #606266;
  font-weight: 500;
}
.picker-table--xhs :deep(.el-table__row) {
  cursor: pointer;
}
.picker-table--xhs :deep(.picker-table-row--folder) {
  background: #fcfcfd;
}
.picker-table--xhs :deep(.picker-table-row--folder:hover > td) {
  background: #f5f9ff !important;
}
.picker-table-folder {
  width: 56px;
  height: 56px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}
.picker-table-folder__icon {
  font-size: 34px;
  color: #3b82f6;
}
.picker-table-thumb {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ebeef5;
  display: block;
  margin: 0 auto;
}
.picker-card {
  width: 100%;
  max-width: 140px;
  min-height: 188px;
  box-sizing: border-box;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: visible;
  cursor: pointer;
  background: #fff;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.picker-card.selected {
  border-color: #409eff;
  box-shadow: 0 0 0 1px #409eff;
}
.picker-card--folder:hover {
  border-color: #c6e2ff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.12);
}
.picker-card__media--folder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
}
.picker-folder-icon {
  font-size: 56px;
  color: #3b82f6;
}
.picker-card__media {
  position: relative;
  width: 100%;
  height: 138px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 8px 8px 0 0;
  background: #f5f7fa;
}
.picker-card__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.picker-card__check {
  position: absolute;
  top: 8px;
  left: 8px;
}
.picker-card__footer {
  flex-shrink: 0;
  border-top: 1px solid #f2f3f5;
  padding: 10px 8px 8px;
  text-align: center;
  background: #fff;
  border-radius: 0 0 8px 8px;
}
.picker-card__footer :deep(.material-name-cell) {
  width: 100%;
}
.picker-card__footer :deep(.material-name-cell__text) {
  font-size: 12px;
}
.picker-card__name {
  padding: 0;
  font-size: 13px;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.picker-card__sub {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  line-height: 1.2;
}
.picker-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-top: 1px solid #e5e7eb;
  background: #fff;
  flex-shrink: 0;
}
.picker-footer__actions {
  display: flex;
  gap: 8px;
}
</style>
